/**
 * EnableFaceIDPrompt — iter195.5.
 *
 * One-time post-login modal that nudges the user to enroll a passkey
 * (Face ID / Touch ID / Windows Hello / Android fingerprint). Shows ONLY when:
 *   1. The user just logged in with phone+password (NOT a session restore on
 *      page reload — gated via sessionStorage 'just_logged_in_password').
 *   2. Browser supports WebAuthn AND a platform authenticator is available
 *      (auto-detected — works on iOS, Android, Mac, Windows, etc).
 *   3. The user has NOT already enrolled a passkey for this account.
 *   4. The user has not dismissed the prompt within the last 30 days.
 *
 * Mounted globally inside <App> so it can fire on any route after login.
 */
import { useCallback, useEffect, useState } from 'react';
import { Fingerprint, Loader2, Shield, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import {
  passkeysSupported,
  platformAuthenticatorAvailable,
  registerPasskey,
  checkHasPasskey,
} from '../lib/passkey';
import { trackPasskeyPromptShown, trackPasskeyPromptDismissed, trackPasskeyEnrolled } from '../lib/analytics';

const DISMISS_KEY = 'passkey_prompt_dismissed_at';
const DISMISS_COOLDOWN_DAYS = 30;
const TRIGGER_FLAG = 'just_logged_in_password';

function deviceLabel() {
  try {
    const ua = navigator.userAgent || '';
    if (/iPhone/i.test(ua)) return 'iPhone';
    if (/iPad/i.test(ua)) return 'iPad';
    if (/Android/i.test(ua)) return 'Android device';
    if (/Macintosh/i.test(ua)) return 'Mac';
    if (/Windows/i.test(ua)) return 'Windows PC';
  } catch { /* ignore */ }
  return 'this device';
}

function biometricLabel() {
  try {
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad/i.test(ua)) return 'Face ID';
    if (/Macintosh/i.test(ua)) return 'Touch ID';
    if (/Windows/i.test(ua)) return 'Windows Hello';
    if (/Android/i.test(ua)) return 'fingerprint';
  } catch { /* ignore */ }
  return 'Face ID';
}

function dismissedRecently() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const last = new Date(ts).getTime();
    if (Number.isNaN(last)) return false;
    const ageDays = (Date.now() - last) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_COOLDOWN_DAYS;
  } catch {
    return false;
  }
}

export default function EnableFaceIDPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const bioLabel = biometricLabel();
  const devLabel = deviceLabel();

  const maybeShow = useCallback(async () => {
    if (!user?.id) return;
    // Was the user JUST logged-in with password (vs restored session)?
    let triggered = false;
    try { triggered = sessionStorage.getItem(TRIGGER_FLAG) === 'true'; } catch { /* ignore */ }
    if (!triggered) return;
    // Consume the trigger flag IMMEDIATELY so a missed check doesn't re-fire
    // expensive async lookups on every navigation within the session.
    try { sessionStorage.removeItem(TRIGGER_FLAG); } catch { /* ignore */ }

    if (dismissedRecently()) return;
    if (!passkeysSupported()) return;
    if (!(await platformAuthenticatorAvailable())) return;
    // Already enrolled? Skip silently.
    const hasOne = await checkHasPasskey(user.phone);
    if (hasOne) return;
    setOpen(true);
    trackPasskeyPromptShown();
  }, [user?.id, user?.phone]);

  useEffect(() => {
    maybeShow();
  }, [maybeShow]);

  const handleEnroll = async () => {
    if (enrolling) return;
    setEnrolling(true);
    try {
      await registerPasskey(devLabel);
      if (user?.phone) {
        try { localStorage.setItem('pref_last_passkey_phone', user.phone); } catch { /* ignore */ }
      }
      trackPasskeyEnrolled('prompt');
      toast.success(`${bioLabel} enabled — sign in with one tap next time!`);
      try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
      setOpen(false);
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        // User cancelled the native prompt — treat like "Not now".
        try { localStorage.setItem(DISMISS_KEY, new Date().toISOString()); } catch { /* ignore */ }
        trackPasskeyPromptDismissed();
        toast.info('No worries — you can enable it later from Profile → Settings.');
        setOpen(false);
      } else {
        toast.error(err?.response?.data?.detail || err?.message || `Could not set up ${bioLabel}`);
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleNotNow = () => {
    try { localStorage.setItem(DISMISS_KEY, new Date().toISOString()); } catch { /* ignore */ }
    trackPasskeyPromptDismissed();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleNotNow(); }}>
      <DialogContent
        className="sm:max-w-sm rounded-2xl p-0 overflow-hidden border-2"
        data-testid="enable-faceid-prompt"
      >
        <button
          type="button"
          onClick={handleNotNow}
          aria-label="Close"
          data-testid="enable-faceid-close"
          className="absolute right-3 top-3 z-10 p-1 rounded-full hover:bg-muted transition"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
        {/* Hero */}
        <div className="px-6 pt-8 pb-4 text-center bg-gradient-to-br from-primary/15 via-primary/5 to-transparent">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-3">
            <Fingerprint className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-display text-xl font-bold leading-tight" data-testid="enable-faceid-title">
            <DialogTitle asChild>
              <span>Skip the password<br />next time?</span>
            </DialogTitle>
          </h2>
          <DialogDescription className="text-sm text-muted-foreground mt-2">
            Sign in with <span className="font-bold text-foreground">{bioLabel}</span> on {devLabel} — one tap, no typing.
          </DialogDescription>
        </div>
        {/* Reassurance */}
        <div className="px-6 py-4 space-y-2">
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Shield className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
            <span>Stays on this device. We never see your fingerprint or face.</span>
          </div>
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Shield className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
            <span>You can still log in with your password anytime.</span>
          </div>
        </div>
        {/* CTAs */}
        <div className="px-6 pb-6 pt-2 space-y-2">
          <Button
            type="button"
            onClick={handleEnroll}
            disabled={enrolling}
            data-testid="enable-faceid-confirm-btn"
            className="w-full h-12 rounded-full font-bold uppercase tracking-wider"
          >
            {enrolling ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting up…</>
            ) : (
              <><Fingerprint className="w-4 h-4 mr-2" /> Enable {bioLabel}</>
            )}
          </Button>
          <button
            type="button"
            onClick={handleNotNow}
            disabled={enrolling}
            data-testid="enable-faceid-dismiss-btn"
            className="w-full h-10 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground transition disabled:opacity-50"
          >
            Not now
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
